import { useCallback, useEffect, useMemo, useRef } from "react";
import { PublicClientApplication, LogLevel, InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { cleanUserProfileCache } from "./auth.user";

const getEnvString = (key) => String(import.meta.env?.[key] ?? "").trim();

const parseApiScopeConfig = (rawValue) => {
    if (!rawValue) {
        return {
            value: [],
            error: "Missing VITE_AUTH_APIS_SCOPE",
        };
    }

    try {
        const parsed = JSON.parse(rawValue);

        if (!Array.isArray(parsed) || !parsed.every((item) => typeof item === "string")) {
            return {
                value: [],
                error: "VITE_AUTH_APIS_SCOPE must be a JSON array of strings",
            };
        }

        return {
            value: parsed,
            error: null,
        };
    } catch {
        return {
            value: [],
            error: "VITE_AUTH_APIS_SCOPE must be valid JSON",
        };
    }
};

const rawEnvConfig = {
    clientId: getEnvString("VITE_AUTH_SPA_CLIENT_ID"),
    authority: getEnvString("VITE_AUTH_AUTHORITY"),
    redirectUri: getEnvString("VITE_AUTH_REDIRECT_URL"),
    postLogoutRedirectUri: getEnvString("VITE_AUTH_POST_LOGOUT_URL"),
    apiBaseUrl: getEnvString("VITE_API_BASE_URL"),
    apiSearchUrl: getEnvString("VITE_API_SEARCH_URL"),
    apiSearchFilterUrl: getEnvString("VITE_API_SEARCH_FILTER__URL"),
};

const parsedApiScopeConfig = parseApiScopeConfig(getEnvString("VITE_AUTH_APIS_SCOPE"));

export const authConfigErrors = [
    !rawEnvConfig.clientId && "Missing VITE_AUTH_SPA_CLIENT_ID",
    !rawEnvConfig.authority && "Missing VITE_AUTH_AUTHORITY",
    !rawEnvConfig.redirectUri && "Missing VITE_AUTH_REDIRECT_URL",
    !rawEnvConfig.postLogoutRedirectUri && "Missing VITE_AUTH_POST_LOGOUT_URL",
    !rawEnvConfig.apiBaseUrl && "Missing VITE_API_BASE_URL",
    !rawEnvConfig.apiSearchUrl && "Missing VITE_API_SEARCH_URL",
    !rawEnvConfig.apiSearchFilterUrl && "Missing VITE_API_SEARCH_FILTER__URL",
    parsedApiScopeConfig.error,
].filter(Boolean);

export const hasRequiredAuthConfig = authConfigErrors.length === 0;

export function msalClientBuilder() {
    if (!hasRequiredAuthConfig) {
        return null;
    }

    const instance = new PublicClientApplication(msalConfig);

    instance.addEventCallback((event) => {
        if (event.eventType === "msal:loginSuccess" ||
            event.eventType === "msal:logoutSuccess") {
            cleanUserProfileCache();
        }
    });

    if (import.meta.env.DEV) {

        // ! Bypass auth for limited development mode only; will be removed soon
        if (import.meta.env.VITE_DEV_BYPASS_AUTH === "true") {
            // console.log("!--> AUTH BYPASS MODE ENABLED");

            const id = "dev-mock";
            const name = "Dev, Mock";
            const email = "dev@mock.local";

            // Account mock
            instance.getAllAccounts = () =>
                [
                    {
                        homeAccountId: id,
                        environment: "localhost",
                        tenantId: "dev",
                        username: email,
                        idTokenClaims: {
                            name,
                            preferred_username: email,
                        }
                    }
                ];
        }
    }

    return instance;
}

export const msalConfig = {
    auth: {
        clientId: rawEnvConfig.clientId,
        authority: rawEnvConfig.authority,
        redirectUri: rawEnvConfig.redirectUri,
        postLogoutRedirectUri: rawEnvConfig.postLogoutRedirectUri,
    },
    cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false,
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                // MSAL Logging for devlopment mode only
                if (import.meta.env.DEV) {
                    // PII should be removed unless you need to check it...
                    if (containsPii) return;
                    // console.log(`[MSAL@L${level}] ${message}`);
                }
            },
            logLevel: LogLevel.Error,
            // logLevel: LogLevel.Verbose,
        },
    },
};

export const apiScope = parsedApiScopeConfig.value;

export const apiConfig = {
    baseUrl: rawEnvConfig.apiBaseUrl,
};

export const apiSearchConfig = {
    searchUrl: rawEnvConfig.apiSearchUrl,
};

export const apiSearchFilterConfig = {
    searchFilterUrl: rawEnvConfig.apiSearchFilterUrl,
};

export const useAuthenticatedApi = () => {
    const { instance, accounts } = useMsal();

    const activeAccount = instance.getActiveAccount();
    const account = useMemo(
        () => activeAccount || accounts?.[0] || null,
        [activeAccount?.homeAccountId, accounts?.[0]?.homeAccountId]
    );
    const accountRef = useRef(account);
    const lastActiveAccountIdRef = useRef("");

    useEffect(() => {
        accountRef.current = account;
    }, [account]);

    useEffect(() => {
        const nextAccountId = account?.homeAccountId || "";
        if (!activeAccount && account && lastActiveAccountIdRef.current !== nextAccountId) {
            instance.setActiveAccount(account);
            lastActiveAccountIdRef.current = nextAccountId;
        }
        if (activeAccount?.homeAccountId) {
            lastActiveAccountIdRef.current = activeAccount.homeAccountId;
        }
    }, [account, activeAccount?.homeAccountId, instance]);

    const callProtectedApi = useCallback(async (url, options = {}) => {
        const selectedAccount = accountRef.current;

        if (!selectedAccount) {
            // Guard in case...
            // console.warn("No account, please sign in first");
            return null;
        }

        // Retrieve token
        let accessToken;

        try {
            const response = await instance.acquireTokenSilent({
                scopes: apiScope,
                account: selectedAccount,
            });

            accessToken = response.accessToken;
        }
        catch (error) {
            // console.error("Token silent acquisition failed:", error);

            if (error?.errorCode === "monitor_window_timeout") {
                // console.log("Silent timeout -> fallback to redirect");

                return instance.acquireTokenRedirect({
                    scopes: apiScope,
                    account: selectedAccount
                });
            }

            if (error instanceof InteractionRequiredAuthError) {
                // console.log("Interaction requared -> fallback to redirect");

                return instance.acquireTokenRedirect({
                    scopes: apiScope,
                    account: selectedAccount
                });
            }

            throw error;
        }

        // Do request
        const isFormData =
            typeof FormData !== "undefined" && options?.data instanceof FormData;
        return axios({
            url,
            ...options,
            headers: {
                ...(isFormData ? {} : { "Content-Type": "application/json" }),
                ...(options.headers || {}),
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }, [instance]);

    return useMemo(
        () => ({
            callProtectedApi,
            apiBaseUrl: apiConfig.baseUrl,
            apiSearchUrl: apiSearchConfig.searchUrl,
            apiSearchFilterUrl: apiSearchFilterConfig.searchFilterUrl,
        }),
        [callProtectedApi]
    );
};

/***
//------------------------------------------
// Snippet: Retrieve user capabilities
//------------------------------------------

// Import useUserCapabilities hook
import { useUserCapabilities } from "../auth.capabilities";

// Use hook to retrieve user capabilities
const {
    hasAdminRights,         // boolean: has admin access
    hasInternalUserRights,  // boolean: internal user access
    hasExternalUserRights,  // boolean: external user access
    role                    // string: "admin", "internal-user", "external-user"
} = useUserCapabilities();

//------------------------------------------
// Snippet: Protected API call
//------------------------------------------

import { useAuthenticatedApi } from "../auth";

const { 
    callProtectedApi,   // Method to call a protected API
    apiBaseUrl          // backend base url (configured inside .env file: VITE_API_BASE_URL)
} = useAuthenticatedApi();

// Example backend call
const response = await callProtectedApi(`${apiBaseUrl}/api/v2/auth/me`);

// backend response payload
const payload = await res.data;

//------------------------------------------
//------------------------------------------
***/

